import { useEffect, useState } from "react";

import { appCRUD } from "../../../actions/app";
import { TableShared } from "../../../shared";
import { char } from "../../../utils";
import { Button, Dropdown, Tag, Modal, Form, Input, InputNumber, Select, message } from "antd";
import { MoreOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";

const { TextArea } = Input;
const { Option } = Select;

export const BuildingContent = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [postsPerPage, setePostPerPage] = useState(10);
  const [nextPage, setNextPage] = useState(0);
  const [value, setValue] = useState("");
  const [index, setIndex] = useState(0);
  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const openNotification = useNotification();

  const [form] = Form.useForm();

  // Load campuses for dropdown
  useEffect(() => {
    appCRUD.loadCampas(100, 0, "", "name")
      .then((resp) => setCampuses(resp.data.results || []))
      .catch(console.error);
  }, []);

  // Load buildings
  useEffect(() => {
    setLoading(true);
    appCRUD
      .loadBuilding(postsPerPage, nextPage, value, "code")
      .then((resp) => {
        setData(resp.data.results);
        setCount(resp.data.count);
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => setLoading(false));
  }, [page, postsPerPage, value, index, nextPage]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editData) {
        await appCRUD.updateBuilding(editData.id, values);
        message.success("Building updated successfully");
      } else {
        await appCRUD.addBuilding(values);
        message.success("Building created successfully");
      }
      setModalOpen(false);
      setEditData(null);
      form.resetFields();
      setIndex(index + 1);
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditData(record);
    form.setFieldsValue({
      campus: record.campus?.id,
      code: record.code,
      name: record.name,
      description: record.description,
      floors: record.floors,
      address: record.address,
      is_active: record.is_active
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Delete Building",
      content: "Are you sure you want to delete this building?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await appCRUD.deleteBuilding(id);
          message.success("Building deleted successfully");
          setIndex(index + 1);
        } catch (error) {
          message.error("Failed to delete building");
        }
      }
    });
  };

  const defaultColumns = [
    { key: "sn", title: "#", dataIndex: "sn", fixed: true, width: 50 },
    { key: "code", title: "Code", dataIndex: "code", sortable: true, width: 100 },
    { key: "name", title: "Building Name", dataIndex: "name", sortable: true, width: 200 },
    { key: "campus", title: "Campus", dataIndex: "campus", width: 150 },
    { key: "floors", title: "Floors", dataIndex: "floors", width: 80 },
    { key: "rooms", title: "Rooms", dataIndex: "rooms", width: 80 },
    { key: "status", title: "Status", dataIndex: "status", width: 80 },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 80,
      render: (record) => (
        <Dropdown
          menu={{
            items: [
              { label: "Edit", key: "edit", icon: <EditOutlined /> },
              { label: "Delete", key: "delete", icon: <DeleteOutlined />, danger: true },
            ],
            onClick: (event) => {
              if (event.key === "edit") handleEdit(record);
              if (event.key === "delete") handleDelete(record.id);
            },
          }}
          placement="bottomRight"
        >
          <Button type="dashed" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  const dataItem = data?.map((dt, i) => ({
    id: dt.id,
    sn: i + 1 + nextPage,
    code: dt.code?.toUpperCase(),
    name: dt.name ? char.toTitleCase(dt.name) : "N/A",
    campus: dt.campus?.name ? char.toTitleCase(dt.campus.name) : "N/A",
    floors: dt.floors || 1,
    rooms: dt.room_count || 0,
    description: dt.description,
    address: dt.address,
    is_active: dt.is_active,
    status: dt.is_active ? (
      <Tag color="success">Active</Tag>
    ) : (
      <Tag color="error">Inactive</Tag>
    ),
  }));

  const button = (
    <Button type="primary" icon={<PlusOutlined />} onClick={() => {
      setEditData(null);
      form.resetFields();
      setModalOpen(true);
    }}>
      New Building
    </Button>
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-12">
            <p className="h6 fs-18 fw-bold text-dark">Buildings</p>
          </div>
          <hr className="mt-3" />
        </div>
        <TableShared
          loading={loading}
          data={dataItem}
          setData={setData}
          page={page}
          setPage={setPage}
          count={count}
          postsPerPage={postsPerPage}
          setePostPerPage={setePostPerPage}
          setNextPage={setNextPage}
          value={value}
          setValue={setValue}
          defaultColumns={defaultColumns}
          button={button}
          showButton={true}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortType={sortType}
          setSortType={setSortType}
          setLoading={setLoading}
        />
      </div>

      <Modal
        title={editData ? "Edit Building" : "Add New Building"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditData(null); form.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="campus" label="Campus" rules={[{ required: true, message: "Please select campus" }]}>
            <Select placeholder="Select Campus" showSearch optionFilterProp="children">
              {campuses.map((c) => (
                <Option key={c.id} value={c.id}>{char.toTitleCase(c.name)}</Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item name="code" label="Building Code" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Input placeholder="e.g., BLD-001" />
            </Form.Item>
            <Form.Item name="floors" label="Number of Floors" rules={[{ required: true }]} style={{ flex: 1 }}>
              <InputNumber min={1} max={50} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <Form.Item name="name" label="Building Name" rules={[{ required: true }]}>
            <Input placeholder="e.g., Science Building" />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input placeholder="Building address" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Building description" />
          </Form.Item>

          <Form.Item name="is_active" label="Status" initialValue={true}>
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>

          <div style={{ textAlign: "right" }}>
            <Button onClick={() => setModalOpen(false)} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editData ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
